<template>
	<div class="attachment-uploader">
		<div v-if="attachments && attachments.length > 0" class="attachment-list mt-10">
			<div v-for="(attachment, index) in attachments" :key="index" class="attachment-item">
				<el-tag size="medium" closable @close="handleRemoveAttachment(attachment)" @click="downloadAttachment(attachment)">
					<i class="el-icon-document"></i>
					{{ attachment.name }}
				</el-tag>
			</div>
		</div>

		<div v-if="!showTabbedPanel" class="add-attachment-button mt-10">
			<el-button type="primary" size="mini" :disabled="loading" @click="showTabbedPanel = true">
				<i class="el-icon-plus"></i> {{ $t('AttachmentUploader.addAttachments') }}
			</el-button>
		</div>

		<div v-if="showTabbedPanel" class="tabbed-panel">
			<el-tabs v-model="activeTab">
				<el-tab-pane :label="$t('AttachmentUploader.filesystem')" name="filesystem">
					<div class="upload-container">
						<el-upload
							ref="upload"
							action=""
							:auto-upload="false"
							:on-change="handleFileChange"
							:file-list="fileList"
							:multiple="true"
							:limit="10"
							:on-exceed="handleExceed"
							:on-remove="handleFileRemove"
							:disabled="loading"
							drag
						>
							<i class="el-icon-upload"></i>
							<div class="el-upload__text">
								{{ $t('AttachmentUploader.dropFilesHere') }} <em>{{ $t('AttachmentUploader.orClickToSelect') }}</em>
							</div>
							<div slot="tip" class="el-upload__tip">{{ $t('AttachmentUploader.attachmentUploadTip') }}</div>
						</el-upload>

						<div v-if="fileList.length > 0" class="upload-actions">
							<span class="selected-files-info">
								<i class="el-icon-document"></i> {{ fileList.length }} {{ fileList.length === 1 ? $t('AttachmentUploader.fileSelected') : $t('AttachmentUploader.filesSelected') }}
							</span>
							<el-button
								size="small"
								type="success"
								:loading="loading"
								@click="uploadFiles"
							>
								<i class="el-icon-upload"></i> {{ $t('AttachmentUploader.uploadNow') }}
							</el-button>
						</div>
					</div>
				</el-tab-pane>
				<!-- https://risolto.atlassian.net/browse/RSLT-4681 -->
				<!-- <el-tab-pane :label="$t('AttachmentUploader.library')" name="library">
					<div class="library-container">
						<div v-if="libraryAttachments.length === 0" class="no-attachments">
							{{ $t('AttachmentUploader.noLibraryAttachments') }}
						</div>
						<div v-else class="library-items">
							<div v-for="(attachment, index) in libraryAttachments" :key="index" class="library-item">
								<el-checkbox v-model="attachment.selected"></el-checkbox>
								<span class="library-item-name">
									<i class="el-icon-document"></i> {{ attachment.name }}
								</span>
							</div>
						</div>
						<div v-if="libraryAttachments.length > 0" class="library-actions">
							<span class="selected-files-info">
								<i class="el-icon-document"></i> {{ selectedLibraryAttachments.length }} {{ selectedLibraryAttachments.length === 1 ? $t('AttachmentUploader.fileSelected') : $t('AttachmentUploader.filesSelected') }}
							</span>
							<el-button
								size="small"
								type="success"
								:loading="loading"
								:disabled="selectedLibraryAttachments.length === 0"
								@click="addFromLibrary"
							>
								{{ $t('AttachmentUploader.addNow') }}
							</el-button>
						</div>
					</div>
				</el-tab-pane> -->
			</el-tabs>

			<div class="panel-actions">
				<el-button size="mini" @click="closeTabbedPanel">
					{{ $t('AttachmentUploader.cancel') }}
				</el-button>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import Logging from '@/logging';
import { attachmentDownload } from '@/api/attachment';

export default {
	name: 'AttachmentUploader',

	props: {
		attachments: {
			type: Array,
			default: () => []
		},
		participant: {
			type: String,
			required: true
		},
		legalEntity: {
			type: String,
			required: true
		},
		loading: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			fileList: [],
			showTabbedPanel: false,
			activeTab: 'filesystem',
			libraryAttachments: []
		};
	},

	computed: {
		selectedLibraryAttachments() {
			return this.libraryAttachments.filter(attachment => attachment.selected);
		}
	},

	created() {
		// In a real implementation, you would fetch library attachments from an API
		// This is just a placeholder for demonstration
		this.fetchLibraryAttachments();
	},

	methods: {
		handleFileChange(file) {
			this.fileList = this.$refs.upload.uploadFiles;
		},

		handleExceed() {
			this.$message.warning(this.$t('AttachmentUploader.attachmentLimitExceeded'));
		},

		async uploadFiles() {
			if (this.fileList.length === 0) return;

			try {
				this.$emit('loading', true);

				for (const file of this.fileList) {
					await this.uploadSingleFile(file);
				}
				this.fileList = [];
				this.$refs.upload.clearFiles();
				this.closeTabbedPanel();
			} catch (error) {
				Logging.error('Error uploading files:', error);
				this.$notify({
					title: this.$t('AttachmentUploader.error'),
					message: this.$t('AttachmentUploader.uploadFailed'),
					type: 'error'
				});
			} finally {
				this.$emit('loading', false);
			}
		},

		async uploadSingleFile(file) {
			// Get signed URL for upload
			const signedUrlPayload = {
				participant: this.participant,
				legal_entity: this.legalEntity,
				extension: file.name.split('.').pop()
			};

			const result = await axios.post(
				`${process.env.VUE_APP_GET_SIGNED_URL_UPLOAD}`,
				signedUrlPayload,
				{
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				}
			);

			const signedUrlData = result.data;
			const { url, fields } = signedUrlData.signed_url;

			// Upload to S3
			const formData = new FormData();
			Object.entries(fields).forEach(([key, val]) => {
				formData.append(key, val);
			});
			formData.append('file', file.raw);

			await axios.post(url, formData);

			// Create attachment object
			const newAttachment = {
				bucket: signedUrlData.bucket_name,
				key: signedUrlData.key,
				name: file.name,
				scheme: 's3',
				content_type: file.raw.type
			};

			this.$emit('attachment-added', newAttachment);
		},

		handleRemoveAttachment(attachment) {
			this.$emit('attachment-removed', attachment);
		},

		handleFileRemove(file, fileList) {
			this.fileList = fileList;
		},

		downloadAttachment(attachment) {
			attachmentDownload(this.participant, attachment);
		},

		closeTabbedPanel() {
			this.showTabbedPanel = false;
			this.fileList = [];
			if (this.$refs.upload) {
				this.$refs.upload.clearFiles();
			}
		},

		fetchLibraryAttachments() {
			// In a real implementation, you would fetch from an API
			// This is just a placeholder for demonstration
			this.libraryAttachments = [
				// Example data - in a real implementation, this would come from an API
				{
					bucket: 'example-bucket',
					key: 'example-key-1',
					name: 'Invoice-2023-001.pdf',
					scheme: 's3',
					content_type: 'application/pdf',
					selected: false
				},
				{
					bucket: 'example-bucket',
					key: 'example-key-2',
					name: 'Contract-2023-A.pdf',
					scheme: 's3',
					content_type: 'application/pdf',
					selected: false
				}
			];
		},

		addFromLibrary() {
			try {
				this.$emit('loading', true);

				// Add selected attachments
				for (const attachment of this.selectedLibraryAttachments) {
					const newAttachment = {
						bucket: attachment.bucket,
						key: attachment.key,
						name: attachment.name,
						scheme: attachment.scheme,
						content_type: attachment.content_type
					};

					this.$emit('attachment-added', newAttachment);
				}

				// Reset selections and close panel
				this.libraryAttachments.forEach(attachment => {
					attachment.selected = false;
				});

				this.closeTabbedPanel();
			} catch (error) {
				Logging.error('Error adding files from library:', error);
				this.$notify({
					title: this.$t('AttachmentUploader.error'),
					message: this.$t('AttachmentUploader.addFailed'),
					type: 'error'
				});
			} finally {
				this.$emit('loading', false);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.attachment-uploader {
  margin-bottom: 20px;
}

.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.attachment-item {
  margin-bottom: 8px;

  .el-tag {
    display: flex;
    align-items: center;
    cursor: pointer;

    i {
      margin-right: 5px;
    }
  }
}

.add-attachment-button {
  margin-bottom: 16px;
}

.tabbed-panel {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
}

.upload-container {
  border-radius: 4px;
  background-color: #fafafa;
  max-width: 480px;
}

.library-container {
  min-height: 200px;
  padding: 16px;
  background-color: #fafafa;
  border-radius: 4px;
  max-width: 480px;
}

.library-items {
  margin-bottom: 16px;
}

.library-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
  }
}

.library-item-name {
  margin-left: 8px;

  i {
    color: #409EFF;
    margin-right: 5px;
  }
}

.no-attachments {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: #909399;
}

.upload-actions,
.library-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding: 0 10px 10px;
}

.panel-actions {
  display: flex;
  justify-content: flex-start;
  margin-top: 16px;
}

.selected-files-info {
  color: #606266;
  font-size: 13px;

  i {
    color: #409EFF;
    margin-right: 5px;
  }
}

.mb-10 {
  margin-bottom: 10px;
}
</style>
